import React, { useEffect, useState } from 'react';
import { PrimaryButton, SecondaryButton } from '../../../shared/ui/Inputs';
import { LargeTile } from '../../../shared/ui/grid/gridsystem';
import { WideLayout } from '../../../shared/layout/widelayout';
import { SimplePopup } from '../../../shared/overlays';
import { usePortalStore } from '../../../stores/portalStore';
import { useLayoutStore } from '../../../stores/portalStore';
import { usePatientsStore } from '../store/patientsStore';
import { PatientsList } from '../usecases/legacy/list';
import { EditPatientModal } from '../components/EditPatientModal';
import { ChangePatientDivisionModal } from '../components/ChangePatientDivisionModal';
import { CircularProgress } from '@mui/material';
import Alert from '@mui/material/Alert';
import { useResponsive } from '../../../hooks/useResponsive';

const OutletHistory = ({
  showDeletePopup,
  showEditPopup,
  showDivisionPopup,
  newPatientForm,
  canManagePatients,
}) => {
  const header = [
    <span className="small" key={'0'}>
      Ações de gerenciamento
    </span>,
    'Nome',
    'E-mail',
    'Empresa',
    'CNPJ',
    'CPF',
    'Telefone/Celular',
    'Status',
  ];
  const fields = [
    'name',
    'email',
    'company',
    'cnpj',
    'cpf',
    'cellphone',
    'status',
  ];
  return (
    <PatientsList
      title={'Relação de pacientes'}
      height={`${window.innerHeight - 350}px`}
      showDeletePopup={showDeletePopup}
      showEditPopup={showEditPopup}
      showDivisionPopup={showDivisionPopup}
      readOnly={false}
      header={header}
      fields={fields}
      newClientAction={
        canManagePatients ? ActionFilters(newPatientForm) : <></>
      }
    ></PatientsList>
  );
};

const ActionFilters = (openPopupStateChange) => {
  return (
    <PrimaryButton
      style={{ width: '150px', marginBottom: '10px' }}
      onClick={() => openPopupStateChange(true)}
    >
      Novo paciente
    </PrimaryButton>
  );
};

const OrderRequestPopup = ({ closePopup }) => (
  <SimplePopup
    width={400}
    height={200}
    outlet={
      <div style={{ display: 'block' }}>
        <p style={{ textAlign: 'center', fontWeight: '500' }}>
          Gostaria de incluir uma nova solicitação para o paciente cadastrado?
        </p>
        <SecondaryButton style={{ marginTop: '10px' }} onClick={closePopup}>
          Não
        </SecondaryButton>
        <PrimaryButton>Sim</PrimaryButton>
      </div>
    }
  ></SimplePopup>
);

const DeletePatientPopup = ({ closePopup, confirmAction, patientStatus }) => {
  const [isLoading, setIsLoading] = React.useState(false);
  const isInactive = String(patientStatus ?? '').toLowerCase() === 'inativo';
  const actionLabel = isInactive ? 'ativar' : 'inativar';
  const confirmLabel = isInactive ? 'Ativar' : 'Inativar';

  return (
    <SimplePopup
      width={400}
      outlet={
        <div
          className="modal-mobile"
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
          }}
        >
          <p style={{ textAlign: 'center', fontWeight: '500' }}>
            {`Tem certeza que deseja ${actionLabel} este paciente?`}
          </p>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <SecondaryButton
              style={{ width: '100px' }}
              onClick={() => closePopup()}
            >
              Cancelar
            </SecondaryButton>
            <PrimaryButton
              style={{ width: '100px' }}
              disabled={isLoading}
              onClick={async () => {
                setIsLoading(true);
                await confirmAction();
                setIsLoading(false);
              }}
            >
              {isLoading && (
                <CircularProgress
                  size={11}
                  sx={{ color: 'white', marginRight: '6px' }}
                />
              )}
              {confirmLabel}
            </PrimaryButton>
          </div>
        </div>
      }
    ></SimplePopup>
  );
};

const QuotaConfirmationPopup = ({ closePopup, confirmAction, quotaItems }) => {
  const [isLoading, setIsLoading] = React.useState(false);
  const quotaCount = Array.isArray(quotaItems) ? quotaItems.length : 0;

  return (
    <SimplePopup
      width={460}
      outlet={
        <div
          className="modal-mobile"
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            gap: '16px',
          }}
        >
          <p style={{ textAlign: 'center', fontWeight: '500', margin: 0 }}>
            Este paciente possui cota ou equipamento alocado. Deseja inativar
            mesmo assim?
          </p>
          {quotaCount > 0 ? (
            <p style={{ textAlign: 'center', margin: 0 }}>
              {`${quotaCount} registro${
                quotaCount === 1 ? '' : 's'
              } encontrado${quotaCount === 1 ? '' : 's'}.`}
            </p>
          ) : null}
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <SecondaryButton
              style={{ width: '100px' }}
              onClick={() => closePopup()}
            >
              Cancelar
            </SecondaryButton>
            <PrimaryButton
              style={{ width: '100px' }}
              disabled={isLoading}
              onClick={async () => {
                setIsLoading(true);
                await confirmAction();
                setIsLoading(false);
              }}
            >
              {isLoading && (
                <CircularProgress
                  size={11}
                  sx={{ color: 'white', marginRight: '6px' }}
                />
              )}
              Inativar
            </PrimaryButton>
          </div>
        </div>
      }
    ></SimplePopup>
  );
};

// Objetos não podem ser renderizados como filho do React (tela em branco), então sempre devolve texto.
const toDisplayText = (value) => {
  if (typeof value === 'string') return value;
  if (value === null || value === undefined)
    return 'Não foi possível completar a operação';
  if (typeof value === 'object' && typeof value.message === 'string')
    return value.message;
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
};

const isHtmlText = (value) =>
  typeof value === 'string' && /^\s*<(!doctype|html)/i.test(value);

const getPatientOperationErrorMessage = (result) => {
  let body = result?.body ?? result?.data ?? result?.response?.data;

  // O backend repassa { httpStatus, body: { message, details } }, então desembrulha o body aninhado.
  if (
    body &&
    !Array.isArray(body) &&
    typeof body === 'object' &&
    body.body &&
    typeof body.body === 'object'
  ) {
    body = body.body;
  }

  if (Number(result?.httpStatus) === 403 && isHtmlText(body?.details)) {
    return 'O cadastro foi bloqueado pelo firewall (403 Access Denied) ao acessar a API de pacientes. Tente novamente e, se persistir, acione o suporte.';
  }

  if (Array.isArray(body)) {
    return (
      <ul>
        {body.map((x, idx) => (
          <li style={{ textAlign: 'left' }} key={idx}>
            {toDisplayText(x)}
          </li>
        ))}
      </ul>
    );
  }

  if (typeof body === 'string' && body.trim()) {
    try {
      const parsed = JSON.parse(body);
      return toDisplayText(parsed?.message ?? parsed?.error ?? body);
    } catch {
      return body;
    }
  }

  return toDisplayText(
    body?.message ??
      body?.error ??
      body?.details ??
      result?.message ??
      result?.error ??
      'Não foi possível completar a operação'
  );
};

const MainPanels = () => {
  if (window.__forceRenderError)
    throw new Error('teste de erro de renderização');

  const { contentWidth, contentHeight } = useLayoutStore((state) => state);
  const { isDesktop } = useResponsive();
  const containerWidth = isDesktop && contentWidth ? contentWidth : '100%';
  const containerHeight =
    isDesktop && contentHeight ? contentHeight - 35 : 'auto';
  const [orderRequestPopup, setOrderRequestPopup] = useState(false);
  const [deletePatientPopup, setDeletePatientPopup] = useState(false);
  const [quotaConfirmationPopup, setQuotaConfirmationPopup] = useState(false);
  const [selectedPatientQuotaItems, setSelectedPatientQuotaItems] = useState(
    []
  );
  const [selectedPatientStatus, setSelectedPatientStatus] = useState('');
  const [editPatientPopup, setEditPatientPopup] = useState(false);
  const [divisionPatient, setDivisionPatient] = useState(null);
  const [patientDivisions, setPatientDivisions] = useState({});
  const [patientSaveSuccess, setPatientSaveSuccess] = useState('');
  const searchTerm = usePatientsStore((state) => state.searchTerm);
  const setSearchTerm = usePatientsStore((state) => state.setSearchTerm);
  const currentUser = usePortalStore((state) => state.currentUser);
  const fetchPatients = usePatientsStore((state) => state.fetchPatients);
  const getSelectedPatientRechargeQuota = usePatientsStore(
    (state) => state.getSelectedPatientRechargeQuota
  );
  const patients = usePatientsStore((state) => state.patients);
  const setId = usePatientsStore((state) => state.setId);
  const setName = usePatientsStore((state) => state.setName);
  const setEmail = usePatientsStore((state) => state.setEmail);
  const setSap = usePatientsStore((state) => state.setSap);
  const setCpf = usePatientsStore((state) => state.setCpf);
  const setCNPJ = usePatientsStore((state) => state.setCNPJ);
  const setCEP = usePatientsStore((state) => state.setCEP);
  const setCity = usePatientsStore((state) => state.setCity);
  const setUF = usePatientsStore((state) => state.setUF);
  const setAddress = usePatientsStore((state) => state.setAddress);
  const setAddressLine2 = usePatientsStore((state) => state.setAddressLine2);
  const setNeighborhood = usePatientsStore((state) => state.setNeighborhood);
  const setNumber = usePatientsStore((state) => state.setNumber);
  const setStatus = usePatientsStore((state) => state.setStatus);
  const setNewPat = usePatientsStore((state) => state.setNewPat);
  const setCellphone = usePatientsStore((state) => state.setCellphone);
  const setPhoneNumber = usePatientsStore((state) => state.setPhoneNumber);
  const setBirthDate = usePatientsStore((state) => state.setBirthDate);
  const setPatientAutofilledFromDatalake = usePatientsStore(
    (state) => state.setPatientAutofilledFromDatalake
  );
  const insertPatient = usePatientsStore((state) => state.insertPatient);
  const deactivatePatient = usePatientsStore(
    (state) => state.deactivatePatient
  );
  const setCurrentSelectPatientCPF = usePatientsStore(
    (state) => state.setCurrentSelectPatientCPF
  );
  const setCurrentSelectPatientSap = usePatientsStore(
    (state) => state.setCurrentSelectPatientSap
  );
  const setCurrentAlertError = usePortalStore(
    (state) => state.setCurrentAlertError
  );

  const setOnPaymentList = usePatientsStore((state) => state.setOnPaymentList);
  const setAdditionalInformation = usePatientsStore(
    (state) => state.setAdditionalInformation
  );
  const canManagePatients = currentUser.isAdmin || currentUser.isManager;
  const canChangeDivision = Boolean(
    currentUser.isAdmin || currentUser.isWhiteMartins
  );
  const getPatientDivisionKey = (patient) =>
    JSON.stringify([
      patient.cnpj,
      patient.sap || patient.cpf || patient.id || patient.name,
    ]);

  useEffect(() => {
    if (!patientSaveSuccess) {
      return undefined;
    }

    const timeout = setTimeout(() => {
      setPatientSaveSuccess('');
    }, 4000);

    return () => clearTimeout(timeout);
  }, [patientSaveSuccess]);

  const fillPatientToEdit = (idx) => {
    const current = patients[idx];
    if (!current) {
      return;
    }
    setId(idx);
    setPatientAutofilledFromDatalake(false);
    setName(current.name);
    setEmail(current.email);
    setSap(current.sap);
    setCpf(
      /^0+$/.test(String(current.cpf ?? '').replace(/\D/g, ''))
        ? ''
        : current.cpf
    );
    setBirthDate(current.birthDate ?? '');
    setCNPJ(current.cnpj);
    setCellphone(current.cellphone);
    setPhoneNumber(current.cellphone2 ?? '');
    setCEP(current.cep);
    setCity(current.city);
    setAddress(current.address);
    setAddressLine2(current.addressLine2);
    setNeighborhood(
      current.neighborhood ?? current.district ?? current.bairro ?? ''
    );
    setUF(current.uf);
    setNumber(current.number);
    setEditPatientPopup(true);
    setNewPat(false);
    setStatus(current.status);
    setOnPaymentList(current.onPaymentList);
    setAdditionalInformation(
      (current.additionalInformation ?? []).map((i) => i.id.toString())
    );
  };

  const newPatientForm = () => {
    setId('');
    setPatientAutofilledFromDatalake(false);
    setName('');
    setEmail('');
    setSap('');
    setCpf('');
    setBirthDate('');
    setCNPJ('');
    setCellphone('');
    setPhoneNumber('');
    setUF('');
    setCEP('');
    setCity('');
    setAddress('');
    setAddressLine2('');
    setNeighborhood('');
    setNumber('');
    setStatus('Solicitado');
    setEditPatientPopup(true);
    setNewPat(true);
    setOnPaymentList(Boolean(currentUser.isAdmin));
    setAdditionalInformation([]);
  };

  const completePatientStatusAction = async () => {
    await fetchPatients();
    resetPatientFields();
    setDeletePatientPopup(false);
    setQuotaConfirmationPopup(false);
    setSelectedPatientQuotaItems([]);
  };

  const performPatientStatusAction = async () => {
    const resp = await deactivatePatient();
    if (resp.httpStatus !== 200) {
      setDeletePatientPopup(false);
      setQuotaConfirmationPopup(false);
      setCurrentAlertError(getPatientOperationErrorMessage(resp));
      return;
    }

    await completePatientStatusAction();
  };

  const deletePatientAction = async () => {
    const isActivatingPatient =
      String(selectedPatientStatus ?? '').toLowerCase() === 'inativo';

    if (isActivatingPatient) {
      await performPatientStatusAction();
      return;
    }

    try {
      const quota = await getSelectedPatientRechargeQuota(currentUser?.cnpj);

      if (quota?.hasQuota) {
        setDeletePatientPopup(false);
        setSelectedPatientQuotaItems(
          Array.isArray(quota.data) ? quota.data : []
        );
        setQuotaConfirmationPopup(true);
        return;
      }
    } catch (error) {
      setDeletePatientPopup(false);
      setCurrentAlertError(getPatientOperationErrorMessage(error));
      return;
    }

    await performPatientStatusAction();
  };

  const confirmQuotaPatientAction = async () => {
    await performPatientStatusAction();
  };

  const resetPatientFields = () => {
    setId(-1);
    setPatientAutofilledFromDatalake(false);
    setName('');
    setEmail('');
    setSap('');
    setCpf('');
    setBirthDate('');
    setCNPJ('');
    setCellphone('');
    setPhoneNumber('');
    setUF('');
    setNumber('');
    setCEP('');
    setCity('');
    setAddress('');
    setAddressLine2('');
    setNeighborhood('');
    setStatus('Solicitado');
  };

  const updatePatient = async () => {
    try {
      const wasCreatingPatient = Boolean(usePatientsStore.getState().newPat);
      const result = await insertPatient();
      const httpStatus = Number(result?.httpStatus);
      if (httpStatus >= 200 && httpStatus < 300) {
        await fetchPatients();
        setEditPatientPopup(false);
        resetPatientFields();
        setSearchTerm('');
        setPatientSaveSuccess(
          wasCreatingPatient
            ? 'Paciente criado com sucesso.'
            : 'Paciente atualizado com sucesso.'
        );
      } else {
        setCurrentAlertError(getPatientOperationErrorMessage(result));
      }
    } catch (error) {
      setCurrentAlertError(getPatientOperationErrorMessage(error));
    }
  };

  return (
    <>
      {divisionPatient && canChangeDivision && (
        <ChangePatientDivisionModal
          patientName={divisionPatient.name}
          initialDivision={
            patientDivisions[getPatientDivisionKey(divisionPatient)] ?? '40'
          }
          onClose={() => setDivisionPatient(null)}
          onSave={(division) => {
            setPatientDivisions((current) => ({
              ...current,
              [getPatientDivisionKey(divisionPatient)]: division,
            }));
            setDivisionPatient(null);
          }}
        />
      )}
      {orderRequestPopup ? (
        <OrderRequestPopup
          closePopup={() => setOrderRequestPopup(false)}
        ></OrderRequestPopup>
      ) : (
        <></>
      )}
      {deletePatientPopup ? (
        <DeletePatientPopup
          closePopup={() => setDeletePatientPopup(false)}
          confirmAction={deletePatientAction}
          patientStatus={selectedPatientStatus}
        ></DeletePatientPopup>
      ) : (
        <></>
      )}
      {quotaConfirmationPopup ? (
        <QuotaConfirmationPopup
          closePopup={() => {
            setSelectedPatientQuotaItems([]);
            setQuotaConfirmationPopup(false);
          }}
          confirmAction={confirmQuotaPatientAction}
          quotaItems={selectedPatientQuotaItems}
        ></QuotaConfirmationPopup>
      ) : (
        <></>
      )}
      {editPatientPopup ? (
        <EditPatientModal
          open={editPatientPopup}
          handleClose={() => {
            resetPatientFields();
            setEditPatientPopup(false);
          }}
          handleSave={updatePatient}
        ></EditPatientModal>
      ) : (
        <></>
      )}
      <LargeTile
        style={{
          maxWidth: containerWidth,
          margin: '15px',
          height: containerHeight,
        }}
      >
        {patientSaveSuccess ? (
          <Alert
            severity="success"
            onClose={() => setPatientSaveSuccess('')}
            sx={{ marginBottom: '12px' }}
          >
            {patientSaveSuccess}
          </Alert>
        ) : null}
        {OutletHistory({
          showDeletePopup: (patient, status) => {
            setCurrentSelectPatientCPF(patient?.cpf ?? '');
            setCurrentSelectPatientSap(patient?.sap ?? '');
            setSelectedPatientQuotaItems([]);
            setSelectedPatientStatus(status);
            setDeletePatientPopup(true);
          },
          showEditPopup: (idx) => {
            fillPatientToEdit(idx);
          },
          showDivisionPopup: canChangeDivision ? setDivisionPatient : undefined,
          newPatientForm,
          canManagePatients,
        })}
      </LargeTile>
    </>
  );
};

export const PatientsPage = () => {
  return <WideLayout outlet={<MainPanels />}></WideLayout>;
};
